import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Space,
  Typography,
  Card,
  Tag,
  Select,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { toastError, toastSuccess } from "../../../utils/toast";
import {
  fetchSocialLinks,
  createSocialLink,
  updateSocialLink,
  deleteSocialLink,
} from "../../../api/socialLinksApi";
import { SOCIAL_PLATFORMS, getSocialLabel } from "../../../utils/socialIcons";

const { Title, Text } = Typography;
const { Option } = Select;

export default function SocialLinksPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchSocialLinks();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toastError("Failed to fetch social links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const showCreateModal = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      platform: "instagram",
      is_active: true,
      display_order: 0,
    });
    setModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      platform: record.platform,
      label: record.label,
      url: record.url,
      display_order: record.display_order || 0,
      is_active: Number(record.is_active) === 1,
    });
    setModalVisible(true);
  };

  const handlePlatformChange = (value) => {
    const current = form.getFieldValue("label");
    if (!current || SOCIAL_PLATFORMS.some((item) => item.label === current)) {
      form.setFieldsValue({ label: getSocialLabel(value) });
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        platform: values.platform,
        label: (values.label || getSocialLabel(values.platform)).trim(),
        url: values.url.trim(),
        display_order: values.display_order ?? 0,
        is_active: values.is_active ? 1 : 0,
      };

      setSaving(true);
      if (editing) {
        await updateSocialLink(editing.id, payload);
        toastSuccess("Social link updated");
      } else {
        await createSocialLink(payload);
        toastSuccess("Social link created");
      }
      setModalVisible(false);
      load();
    } catch (err) {
      if (!err.errorFields) toastError("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSocialLink(id);
      toastSuccess("Social link deleted");
      load();
    } catch {
      toastError("Delete failed");
    }
  };

  const columns = [
    {
      title: "Platform",
      dataIndex: "platform",
      render: (value) => getSocialLabel(value),
    },
    { title: "Label", dataIndex: "label" },
    {
      title: "URL",
      dataIndex: "url",
      render: (url) => (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      ),
    },
    {
      title: "Order",
      dataIndex: "display_order",
      width: 90,
      sorter: (a, b) => (a.display_order || 0) - (b.display_order || 0),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      width: 110,
      render: (isActive) => {
        const active = Number(isActive) === 1;
        return (
          <Tag icon={active ? <CheckOutlined /> : <CloseOutlined />} color={active ? "success" : "error"}>
            {active ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            type="text"
            icon={<EditOutlined style={{ color: "#1890ff" }} />}
            onClick={() => showEditModal(record)}
          />
          <Popconfirm title="Delete this social link?" onConfirm={() => handleDelete(record.id)} okText="Delete">
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={4} style={{ margin: 0 }}>
              Social Media
            </Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
              New Link
            </Button>
          </div>
        }
        bordered={false}
      >
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          bordered
        />
      </Card>

      <Modal
        title={editing ? "Edit Social Link" : "New Social Link"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="platform" label="Platform" rules={[{ required: true }]}>
            <Select onChange={handlePlatformChange}>
              {SOCIAL_PLATFORMS.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="label" label="Label">
            <Input placeholder="Instagram" />
          </Form.Item>
          <Form.Item
            name="url"
            label="URL"
            rules={[{ required: true, type: "url", message: "Enter a valid URL including https://" }]}
          >
            <Input placeholder="https://" />
          </Form.Item>
          <Form.Item name="display_order" label="Display order">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
